import Color from 'color';
import strings from 'owa-strings/owa-mini-common-strings';
import { AccessibleTextScheme, ColorScheme } from './ColorScheme';
import { getOptimalTextColor } from './OptimalTextColorCache';
import {
    DEFAULT_THEME_PRIMARY_COLOR,
    ACCEPTED_COLOR,
    CONFLICT_COLOR,
} from 'mini-sass-mixins/lib/js/colorConstants';

const shadeValues = {
    schemeDarker: -44,
    schemeDark: -24,
    schemeDarkAlt: -10,
    schemePrimary: 0,
    schemeSecondary: 12,
    schemeTertiary: 40,
    schemeLight: 70,
    schemeLighter: 84,
    schemeLighterAlt: 96,
};

/**
 * Return whether the color scheme's primary color is the default color or not
 */
export function isDefaultColor(colorScheme: ColorScheme): boolean {
    return colorScheme.schemePrimary === DEFAULT_THEME_PRIMARY_COLOR;
}

/**
 * These are the default 9 schemePrimary colors for users to pick from without picking their own custom hex values
 */
const defaultPrimaryColors = {
    /* Calendar colors */
    [DEFAULT_THEME_PRIMARY_COLOR]: () => strings.blueCalendarColorText, // blue
    '#498205': () => strings.greenCalendarColorText, // green
    '#C50F1F': () => strings.redCalendarColorText, // Red
    '#003966': () => strings.navyCalendarColorText, // Navy
    '#69797E': () => strings.grayCalendarColorText, // Gray
    '#038387': () => strings.tealCalendarColorText, // Teal
    '#E3008C': () => strings.magentaCalendarColorText, // Magenta
    '#DA3B01': () => strings.orangeCalendarColorText, // Orange
    '#744DA9': () => strings.violetCalendarColorText, // Violet

    /* RSVP */
    [ACCEPTED_COLOR]: () => 'owa-color-event-accepted',
    [CONFLICT_COLOR]: () => 'owa-color-event-conflict',
};

const cachedColorSchemes = {};

/**
 * Generates the default color schemes and returns them as an array
 */
export function generateDefaultColorSchemes(): ColorScheme[] {
    return Object.keys(defaultPrimaryColors).map(generateColorScheme);
}

// All color schemes present
let defaultColorSchemes: ColorScheme[];

/**
 * gets the list of default color schemes
 */
export function getDefaultColorSchemes(): ColorScheme[] {
    if (!defaultColorSchemes) {
        defaultColorSchemes = generateDefaultColorSchemes();
    }
    return defaultColorSchemes;
}

const MINI_DEFAULT_COLOR_SCHEME_INDEX = 0;
/**
 * gets the default color schemes for Mini
 */
export const getDefaultMiniColorScheme = () =>
    getDefaultColorSchemes()[MINI_DEFAULT_COLOR_SCHEME_INDEX];

/**
 * Get the color scheme for a given primary color scheme, with caching.
 * @param primaryHexColor the primary color for the scheme.
 */
export function generateColorScheme(primaryHexColor: string): ColorScheme {
    return cachedColorSchemes[primaryHexColor]
        ? cachedColorSchemes[primaryHexColor]
        : (cachedColorSchemes[primaryHexColor] = generateColorSchemeFromPrimaryColor(
              primaryHexColor
          ));
}

/**
 * We dynamically generate a color scheme given a primary color value by taking the primary color and shading it with a % of black and % of white
 *
 * The percentages of white and black come from shadeValues, and create a ColorScheme where the given primary color corresponds to schemePrimary, with each darker
 * shade mixing extra black and each lighter shade mixing extra white.
 *
 * Code from universal color generation is here:
 * https://msasg.visualstudio.com/_search?type=code&lp=custom-Collection&text=CalendarColorShadeGenerator&result=DefaultCollection%2FOfficeSourceDepot%2Fdevmain%2Fdevmain%2Fhxcalendarui%2Fxaml%2FSupport%2FcolorShadeGenerator%2FCalendarColorShadeGenerator.cpp&preview=1&_a=search
 *
 * @param primaryHexColor the string value of the color to use as primary
 */
function generateColorSchemeFromPrimaryColor(primaryHexColor: string): ColorScheme {
    const primaryColor = Color(primaryHexColor);
    const colorScheme: ColorScheme = {
        text: '',
        textAlt: '',
        accessibleText: {
            schemePrimaryBackground: '',
            schemeLighterBackground: '',
            msColorWhiteBackground: '',
            msColorNeutralLighterBackground: '',
        },
        schemeDarker: shadeColor(primaryColor, shadeValues.schemeDarker).hex(),
        schemeDark: shadeColor(primaryColor, shadeValues.schemeDark).hex(),
        schemeDarkAlt: shadeColor(primaryColor, shadeValues.schemeDarkAlt).hex(),
        schemePrimary: primaryColor.hex(),
        schemeSecondary: shadeColor(primaryColor, shadeValues.schemeSecondary).hex(),
        schemeTertiary: shadeColor(primaryColor, shadeValues.schemeTertiary).hex(),
        schemeLight: shadeColor(primaryColor, shadeValues.schemeLight).hex(),
        schemeLighter: shadeColor(primaryColor, shadeValues.schemeLighter).hex(),
        schemeLighterAlt: shadeColor(primaryColor, shadeValues.schemeLighterAlt).hex(),
        colorName: () => getColorName(primaryColor.hex()),
    };

    colorScheme.accessibleText = calculateAccessibleText(colorScheme);
    colorScheme.text = colorScheme.accessibleText.schemePrimaryBackground;
    colorScheme.textAlt = colorScheme.accessibleText.schemeLighterBackground;

    return colorScheme;
}

/**
 * Calculates accessible passing text colors for various background colors. Uses the color scheme as a base and adjusts the color as necessary to get
 * minimum contrast for the given backgrounds.
 * @param colorScheme the color scheme
 */
function calculateAccessibleText(colorScheme: ColorScheme): AccessibleTextScheme {
    const whiteColor = '#FFFFFF';
    const colorNeutral = '#F4F4F4';

    return {
        schemePrimaryBackground: getOptimalTextColor(
            colorScheme.schemePrimary,
            colorScheme,
            whiteColor
        ),
        schemeLighterBackground: getOptimalTextColor(
            colorScheme.schemeLighter,
            colorScheme,
            whiteColor
        ),
        msColorWhiteBackground: getOptimalTextColor(whiteColor, colorScheme, whiteColor),
        msColorNeutralLighterBackground: getOptimalTextColor(colorNeutral, colorScheme, whiteColor),
    };
}

function getColorName(color: string): string {
    return defaultPrimaryColors[color] ? defaultPrimaryColors[color]() : color;
}

/**
 * Shades in black or white into the given RGB value and returns a new value.
 * @param color the Color to shade
 * @param amount the amount to shade, [-100, 100]. Negative shades in black, positive shades in white i.e. -100 returns black.
 */
function shadeColor(color: Color, amount: number): Color {
    let newColor = Color(color.rgb());
    if (amount === 0) {
        return newColor;
    } else if (amount < 0) {
        // Shade by mixing with [amount]% black.  Black is at V = 0, for any H and S values.
        newColor = newColor.value(color.value() * (1 + amount / 100.0));
    } else {
        // Tint by mixing with [amount]% white.  White is the point where V = 1 and S = 0 for any H value.
        newColor = newColor.value(color.value() + ((100 - color.value()) * amount) / 100.0);
        newColor = newColor.saturationv(color.saturationv() * (1 - amount / 100));
    }

    newColor = newColor.saturationv(clamp(newColor.saturationv()));
    newColor = newColor.value(clamp(newColor.value()));
    return newColor;
}

function clamp(value: number): number {
    if (value < 0) {
        return 0;
    }
    if (value > 100) {
        return 100;
    }

    return value;
}

/**
 * Adds the given opacity to the given color value and returns a string representation of the result
 */
export function addOpacityToColor(colorString: string, opacity: number): string {
    const color = Color(colorString);
    return color.alpha(opacity).toString();
}

/**
 * Computes the result of overlapping multiple colors on top of each other. This only has meaning if the colors
 * overlapping have opacity to them- if the top color has alpha = 1.0, the result will just be the top color.
 *
 * this is called alpha compositing: https://en.wikipedia.org/wiki/Alpha_compositing, an implementation of the "over" operation
 * @param colors the colors to overlap. Descending order (first color is on top, second color is the second from the top, etc.)
 * @returns string representation of the resulting color.
 */
export function computeResultingOverlapColor(...colors: string[]): string {
    let total = Color(colors[colors.length - 1]);

    for (let i = colors.length - 2; i >= 0; i--) {
        const top = Color(colors[i]);
        const totalAlpha = top.alpha() + total.alpha() * (1 - top.alpha());
        total = Color({
            r: Math.floor(
                (top.alpha() * top.red() + total.alpha() * (1 - top.alpha()) * total.red()) /
                    totalAlpha
            ),
            g: Math.floor(
                (top.alpha() * top.green() + total.alpha() * (1 - top.alpha()) * total.green()) /
                    totalAlpha
            ),
            b: Math.floor(
                (top.alpha() * top.blue() + total.alpha() * (1 - top.alpha()) * total.blue()) /
                    totalAlpha
            ),
            alpha: totalAlpha,
        });
    }

    return total.toString();
}
